import style from '../titularArrendatarioResumo/titularArrendatarioResumo.module.scss';
import InformacaoBasica from '../../../../components/informacaoBasica/informacaoBasica';
import OperacaoDetalhes from './components/operacaoDetalhes/operacaoDetalhes';
import { BrCheckbox, BrDivider, BrLoading } from '@govbr-ds/react-components';
import { IPLGResumo } from '../../../../interfaces/IOperacaoPLG';
import { Tab } from 'react-dsgov';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../lib/backEndRequests';
import { useEffect, useState } from 'react';
import { formatarNrProcesso } from '../../../../utils/formatNrProcesso';
import { formatCurrency } from '../../../../utils/formatCurrency';
import BaseDeCalculoAccordionList from './components/baseDeCalculoAccordionList/baseDeCalculoAccordionList';
import { IOperacaoOutrosRegimesResumo, IRetornoOutrosRegimesResumo } from '../../../../interfaces/IOperacoes';
import Paginacao from '../../../../components/paginacao/paginacao';
import { useNotificacao } from '../../../../hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IProps {
  isEtapaResumo?: boolean;
  noBorder?: boolean;
  declaracaoSelecionada?: string;
  abaSelecionada: number;
  setAbaSelecionada: React.Dispatch<React.SetStateAction<number>>;
  periodoDeclaracao: string;
}

interface IResumo {
  value: string;
  label: string;
  show?: boolean;
}

interface IHeader {
  field: string;
  label: string;
  format?: Function;
}

function TitularArrendatarioResumo({
  isEtapaResumo = true,
  noBorder = false,
  declaracaoSelecionada,
  abaSelecionada,
  setAbaSelecionada,
  periodoDeclaracao,
}: IProps) {
  const DEFAULT_PAGE_SIZE = 10;
  const PAGE_SIZE_OPTIONS = [3, 5, 10];

  const urlParams = new URLSearchParams(window.location.search);
  const declaracaoRaiz = urlParams.get('id') || '';

  const [resumoListagemPLG, setResumoListagemPLG] = useState<IPLGResumo>();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCount, setPageCount] = useState(0);
  const [pageItemCount, setPageItemCount] = useState(0);
  const [listagemOutrosRegimesResumo, setListagemOutrosRegimesResumo] = useState<IOperacaoOutrosRegimesResumo[]>([]);
  const [isExibirProcessoSemPreenchimento, setIsExibirProcessoSemPreenchimento] = useState(false);

  const [declaracaoId, setDeclaracaoId] = useState<string>(declaracaoRaiz);

  const mostrarNotificacao = useNotificacao();

  const INFO_RESUMO: IResumo[] = [
    {
      value: formatCurrency(resumoListagemPLG?.valorTotalOperacoes || ''),
      label: 'Valor Total das Operações',
    },
  ];

  const INFO_CAMPOS: IHeader[] = [
    {
      field: 'idOperacao',
      label: 'Id Operação',
    },
    {
      field: 'numeroProcesso',
      label: 'Processo Minerário',
      format: formatarNrProcesso,
    },
    { field: 'substanciaDto.nome', label: 'Substância' },
    {
      field: 'municipioDto',
      label: 'Município/UF',
      format: (value: any) => `${value.nome}/${value.uf}`,
    },
    {
      field: 'valorOperacao',
      label: 'Valor Operação',
      format: formatCurrency,
    },
  ];

  const { getDeclaracaoResumoPLGById, getResumoOutrosRegimesByDeclaracaoId } = useBackEndRequests();

  const {
    data: listagemResumoPLG,
    isLoading: PLGLoading,
    refetch: refetchResumoPLG,
  } = useQuery<IPLGResumo, IErrorWithSentryId>({
    queryKey: ['getlistagemResumoPLG', declaracaoId],
    queryFn: () => getDeclaracaoResumoPLGById(declaracaoId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: retornoListagemOutrosRegimesResumo,
    isLoading: retornoListagemOutrosRegimesResumoLoading,
    isFetching: retornoListagemOutrosRegimesResumoFetching,
    refetch: refetchResumoOutrosRegimes,
    error,
  } = useQuery<IRetornoOutrosRegimesResumo, IErrorWithSentryId>({
    queryKey: [
      'getlistagemRetornoOutrosRegimesResumo',
      declaracaoId,
      pageNumber,
      pageSize,
      isExibirProcessoSemPreenchimento,
    ],
    queryFn: () =>
      getResumoOutrosRegimesByDeclaracaoId(declaracaoId, pageNumber - 1, pageSize, isExibirProcessoSemPreenchimento),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const INFO_RESUMO_OUTROS_REGIMES: IResumo[] = [
    {
      value: formatCurrency(retornoListagemOutrosRegimesResumo?.content[0].valorOperacao || 0),
      label: 'Valor Total das Operações',
    },
    {
      value: formatCurrency(retornoListagemOutrosRegimesResumo?.content[0].deducoes || 0),
      label: 'Deduções',
    },
    {
      value: formatCurrency(retornoListagemOutrosRegimesResumo?.content[0].baseCalculo || 0),
      label: 'Base de Cálculo',
    },
  ];

  const INFO_CAMPOS_OUTROS_REGIMES: IHeader[] = [
    {
      field: 'numeroProcesso',
      label: 'Processo Minerário Nº',
      format: formatarNrProcesso,
    },
    {
      field: 'substancia',
      label: 'Substância',
      format: (value: any) => value.nome,
    },
    {
      field: 'municipio',
      label: 'Município/UF',
      format: (value: any) => `${value.nome}/${value.uf}`,
    },
    {
      field: 'hasFatoGeradorCFEM',
      label: 'Houve Fato Gerador CFEM',
      format: (value: any) => (value ? 'Sim' : 'Não'),
    },
    {
      field: 'hasProducao',
      label: 'Houve Produção de Bem Mineral',
      format: (value: any) => (value ? 'Sim' : 'Não'),
    },
  ];

  useEffect(() => {
    setDeclaracaoId(declaracaoSelecionada ?? declaracaoRaiz);
  });

  useEffect(() => {
    if (retornoListagemOutrosRegimesResumo) {
      setPageItemCount(retornoListagemOutrosRegimesResumo.page.totalElements);
      setPageCount(retornoListagemOutrosRegimesResumo.page.totalPages);
      setListagemOutrosRegimesResumo(retornoListagemOutrosRegimesResumo.content);
    } else if ((retornoListagemOutrosRegimesResumo !== undefined && !retornoListagemOutrosRegimesResumo) || !!error) {
      const errorResponse = error?.response?.data as any;

      mostrarNotificacao({
        tipo: 'danger',
        conteudo: errorResponse?.message || 'Erro ao recuperar os dados',
        ...(errorResponse && { complementoSentryId: errorResponse }),
      });
    }
  }, [retornoListagemOutrosRegimesResumo, error]);

  useEffect(() => {
    if (declaracaoSelecionada) {
      setDeclaracaoId(declaracaoSelecionada);

      refetchResumoPLG();
      refetchResumoOutrosRegimes();
    }
  }, [declaracaoSelecionada]);

  const onPageNumberChange = (number: number) => {
    setPageNumber(number);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const onIsExibirProcessoSemPreenchimentoChange = (value: boolean) => {
    setIsExibirProcessoSemPreenchimento(value);
    setPageNumber(1);
  };

  useEffect(() => {
    if (listagemResumoPLG) {
      setResumoListagemPLG(listagemResumoPLG);
    }
  }, [listagemResumoPLG]);

  function getNestedFieldValue(obj: any, field: string): any {
    return field.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  function componenteResumoPLG() {
    return (
      <>
        <div className={style.resumo}>
          {INFO_RESUMO.map((field, index) => (
            <>{(field.show ?? true) && <InformacaoBasica key={index} titulo={field.label} conteudo={field.value} />}</>
          ))}
        </div>
        {resumoListagemPLG?.operacoes.map((data) => (
          <div className={style.processoSection}>
            <div className={style.tableTitle}>
              <div className={style.tableTitleContentContainer}>
                {INFO_CAMPOS.map((value, index) => (
                  <div className={style.tableTitleContent} key={index}>
                    <span>{value.label}:</span>
                    <p>
                      {value.format && value.field
                        ? value.format(getNestedFieldValue(data, value.field))
                        : (getNestedFieldValue(data, value.field) ?? '')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {data.itensOperacao?.map((value, index) => (
              <OperacaoDetalhes key={index} data={value} />
            ))}
            {data.itensOperacao.length === 0 && (
              <OperacaoDetalhes
                data={{
                  cpfCnpj: '',
                  tipo: 'SEM_OPERACAO',
                  rejeitosEstereis: false,
                  cadeiaProdutiva: '',
                  itensOperacao: [],
                  totalQuantidade: 0,
                  totalPrecoUnitario: 0,
                  totalValorOperacao: 0,
                }}
              />
            )}
            <div className={style.observacoes}>
              <span>Observações: </span>
              <p> {data.observacoes || 'Não há observações referente a esta operação'} </p>
            </div>
            <div className={style.divider}>
              <BrDivider />
            </div>
          </div>
        ))}
        {resumoListagemPLG?.isDeclaraDemaisProcessosSemOperacoes && !isEtapaResumo && (
          <div className={style.checkBoxDeclaracao}>
            <BrCheckbox
              className={style.checkbox}
              label={
                'Declaro que para os processos não declarados não houve movimentação ou produção para esse período.'
              }
              checked={listagemResumoPLG?.isDeclaraDemaisProcessosSemOperacoes}
              disabled={true}
            />
          </div>
        )}
      </>
    );
  }

  function componenteResumoOutrosRegimes() {
    return (
      <>
        <div className={style.resumo}>
          {INFO_RESUMO_OUTROS_REGIMES.map((field, index) => (
            <>{(field.show ?? true) && <InformacaoBasica key={index} titulo={field.label} conteudo={field.value} />}</>
          ))}
        </div>
        <div className={style.checkboxContainer}>
          <div className={style.checkboxExibirProcessos}>
            <div
              className={`br-switch icon ${style.switch}`}
              onClick={() => onIsExibirProcessoSemPreenchimentoChange(!isExibirProcessoSemPreenchimento)}
              onKeyDown={() => onIsExibirProcessoSemPreenchimentoChange(!isExibirProcessoSemPreenchimento)}
              role='switch'
              aria-checked={isExibirProcessoSemPreenchimento}
              tabIndex={0}
            >
              <label className={style.labelSwitch}>Exibir processos sem preenchimento: </label>
              <input
                id='switch-icon'
                type='checkbox'
                name='switch-icon'
                checked={isExibirProcessoSemPreenchimento}
                role='switch'
              />
              <label className={style.labelSwitch}>{isExibirProcessoSemPreenchimento ? 'Sim' : 'Não'}</label>
            </div>
          </div>
        </div>
        {listagemOutrosRegimesResumo.map((data) =>
          data.operacoes.map((operacao, indexOp) => (
            <div className={style.processoSection} key={operacao.numeroProcesso + indexOp}>
              <div className={style.tableTitle}>
                <div className={style.tableTitleContentContainer}>
                  {INFO_CAMPOS_OUTROS_REGIMES.map((value, index) => (
                    <div className={style.tableTitleContent} key={index}>
                      <span>{value.label}:</span>
                      <p>
                        {value.format && value.field
                          ? value.format(operacao[value.field as keyof typeof operacao] ?? '')
                          : (value.field ?? '')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <BaseDeCalculoAccordionList listaComponentesBaseCalculoResumo={operacao.baseCalculo} />
              <div className={style.divider}>
                <BrDivider />
              </div>
            </div>
          ))
        )}
        {listagemOutrosRegimesResumo.length !== 0 && (
          <div className={style.paginacao}>
            <Paginacao
              pageCount={pageCount}
              itemCount={pageItemCount}
              selectedPage={pageNumber}
              onChangePage={onPageNumberChange}
              onChangeSizePage={onPageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              defaultPageSize={pageSize}
              showPageCombo={true}
            />
          </div>
        )}
        {listagemOutrosRegimesResumo[0]?.isDeclaraTodosProcessosSemOperacoes && !isEtapaResumo && (
          <div className={style.checkBoxDeclaracao}>
            <BrCheckbox
              className={style.checkbox}
              label={`Declaro que todos os processos desta declaração estão sem movimentação no período de ${periodoDeclaracao}.`}
              checked={listagemOutrosRegimesResumo[0].isDeclaraTodosProcessosSemOperacoes}
              disabled={true}
            />
          </div>
        )}
      </>
    );
  }

  const containerClass = !noBorder ? style.containerBorder : style.container;

  return (
    <div className={containerClass} data-testid='componente-resumo'>
      {isEtapaResumo && <h2 className={style.titulo}>Resumo da Declaração</h2>}
      <Tab density='small' current={abaSelecionada} onTabChange={(currentTab) => setAbaSelecionada(currentTab)}>
        <Tab.Content active={false} title='Concessão/Licenciamento/Guia de Utilização'>
          {retornoListagemOutrosRegimesResumoLoading ||
          retornoListagemOutrosRegimesResumoFetching ||
          !retornoListagemOutrosRegimesResumo ||
          retornoListagemOutrosRegimesResumo?.content.length === 0 ? (
            <div className={style.tableLoading}>
              <BrLoading large label='Carregando...' />
            </div>
          ) : (
            componenteResumoOutrosRegimes()
          )}
        </Tab.Content>
        <Tab.Content title='Permissão de Lavra Garimpeira'>
          {PLGLoading ? (
            <div className={style.tableLoading}>
              <BrLoading large label='Carregando...' />
            </div>
          ) : (
            componenteResumoPLG()
          )}
        </Tab.Content>
      </Tab>
    </div>
  );
}

export default TitularArrendatarioResumo;
